import React, { useCallback } from 'react';
import { cn } from '@/shadcn/lib/utils';

interface GridItemWrapperProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  item: T;
  onItemClick: (item: T) => void;
  children: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}

const GridItemWrapper = <T,>({
  item,
  onItemClick,
  children,
  ariaLabel,
  disabled = false,
  className,
  ...props
}: GridItemWrapperProps<T>) => {
  const handleClick = useCallback(() => {
    if (!disabled) onItemClick(item);
  }, [item, onItemClick, disabled]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
      event.preventDefault();
      onItemClick(item);
    }
  }, [item, onItemClick, disabled]);

  return (
    <div
      className={cn(
        'cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500', // sensible defaults
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      role="button"
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
};

GridItemWrapper.displayName = 'GridItemWrapper';

export { GridItemWrapper };
export type { GridItemWrapperProps };
